import { UserRoles } from "../../../enums/UserRoles";
import { User } from "../../../types/user";

// Internal-staff roles that grant admin-side access. Mirrors the server's
// ADMIN_SIDE_ROLES (server/auth/externalPrincipals.ts) - keep both lists in
// sync when either changes. POSTOP added 2026-09-24 alongside the server, in
// the same PR: letting them drift would misclassify a POSTOP session in
// isAccountantOnlyUser. OR added 2026-10-05 the same way (OR Board, owner
// decision D5).
const STAFF_ROLES: UserRoles[] = [
  UserRoles.ADMIN,
  UserRoles.COORDINATOR,
  UserRoles.PATIENT_EXPERIENCE,
  UserRoles.MARKETING,
  UserRoles.SURGEON,
  UserRoles.PREOP,
  UserRoles.POSTOP,
  UserRoles.OR,
  UserRoles.QUOTE_VIEWER,
];

// "Coordinator-grade access": the roles that may open the coordinator pages
// and use the coordinator in-page actions. Mirrors the server's
// COORDINATOR_ACCESS_ROLES (server/graphql/requireAdmin.ts) - keep both lists
// in sync. PREOP, POSTOP and OR joined PATIENT_EXPERIENCE here on 2026-10-05
// (OR Board, owner decision D5). This is an ACCESS list, never a "this user is
// a coordinator" check: lead-owner / coordinator pickers, presence and
// anything else that assigns or counts coordinators keep checking
// UserRoles.COORDINATOR directly. MainMenu's menu choice is separate too: it
// keeps its own coordinator-menu check so these roles keep focused menus.
export const COORDINATOR_ACCESS_ROLES: ReadonlyArray<UserRoles> = [
  UserRoles.ADMIN,
  UserRoles.COORDINATOR,
  UserRoles.PATIENT_EXPERIENCE,
  UserRoles.PREOP,
  UserRoles.POSTOP,
  UserRoles.OR,
];

export function hasCoordinatorAccessRole(
  roles: ReadonlyArray<string> | null | undefined,
): boolean {
  return (roles ?? []).some((r) =>
    (COORDINATOR_ACCESS_ROLES as ReadonlyArray<string>).includes(r),
  );
}

// True when the signed-in user is a pure external ACCOUNTANT principal — the
// CPA/bookkeeper role added in PR #2336 — holding no staff role alongside it.
// addUser/updateUser reject any role set that combines ACCOUNTANT with a
// staff role (assertCoherentRoleSet, this PR), so the two shouldn't appear
// together in practice, but this predicate checks explicitly rather than
// trusting that invariant — the client stays correct even if it's ever
// violated. Shared by every surface that needs to narrow the accountant's
// experience: the sidebar menu, the dashboard shell's admin-only chrome, and
// write-action gating on read-only pages like QuickBooks Settings.
export function isAccountantOnlyUser(
  user: Pick<User, "roles"> | null | undefined,
): boolean {
  const roles = user?.roles ?? [];
  if (!roles.includes(UserRoles.ACCOUNTANT)) return false;
  return !STAFF_ROLES.some((r) => roles.includes(r));
}

// The roles that win MainMenu's menu precedence over OR: ADMIN and the
// coordinator menu (COORDINATOR, PATIENT_EXPERIENCE) first, then MARKETING,
// SURGEON, PREOP and POSTOP. QUOTE_VIEWER is not in that chain, so it never
// takes the menu away from OR.
const ROLES_ABOVE_OR_IN_MENU: UserRoles[] = [
  UserRoles.ADMIN,
  UserRoles.COORDINATOR,
  UserRoles.PATIENT_EXPERIENCE,
  UserRoles.MARKETING,
  UserRoles.SURGEON,
  UserRoles.PREOP,
  UserRoles.POSTOP,
];

// True for exactly the session MainMenu gives `orItems` (2026-10-05, OR
// Board): it holds OR and none of the roles above. Drives the OR's landing
// page (the calendar) in AdminHomeRedirect.
export function isOrOnlyUser(
  user: Pick<User, "roles"> | null | undefined,
): boolean {
  const roles = user?.roles ?? [];
  if (!roles.includes(UserRoles.OR)) return false;
  return !ROLES_ABOVE_OR_IN_MENU.some((r) => roles.includes(r));
}
